class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.reload = this.dataset.reload === "true" ? true : false;
    this.promotionAutoAdds = this.getPromotionAutoAdds();
    this.hideAutoAddedUpsells();

    this.events = [
      {
        el: this.querySelector(".product-form__container product-form"),
        type: "submit",
        callback: this.submitHandler.bind(this),
      },
    ];

    if (this.querySelector("product-form-sticky")) {
      this.events.push({
        el: this.querySelector("product-form-sticky product-form"),
        type: "submit",
        callback: this.submitHandler.bind(this),
      });
    }

    const upsells = this.querySelector(".upsell-bundle__products");
    if (upsells) {
      this.events.push({
        el: upsells,
        type: "click",
        callback: this.upsellAddHandler.bind(this),
      });
    }
  }

  submitHandler(e) {
    e.preventDefault();
    const formData = new FormData(e.target);
    const items = [];
    const item = {};

    for (const [key, value] of formData.entries()) {
      if (!key.includes("properties")) {
        item[key] = value;
      }
    }

    const properties = {};
    const prop_regex = /\[(.*?)\]/;
    for (const [key, value] of formData.entries()) {
      if (key.includes("properties")) {
        const property_key = key.match(prop_regex)[1];
        properties[property_key] = value;
      }
    }

    const personalisation_target = this.querySelector(".extra-options__container");
    if (personalisation_target) {
      const personalisationFormData = new FormData(personalisation_target);
      for (const [key, value] of personalisationFormData.entries()) {
        if (key.includes("properties")) {
          const property_key = key.match(prop_regex)[1];
          properties[property_key] = value;
        }
      }
    }

    item.properties = properties;

    item.product_id = this.dataset.productId;
    items.push(item);

    items.push(...this.getActivePromotionItems(items));

    const data = {
      type: "add",
      source: "product-page",
      sections: ["announcement-banner"],
      sections_url: "/",
      items,
      submitter: e.submitter,
      reload: this.reload,
    };
    updateCart(data);
  }

  upsellAddHandler(e) {
    const submitter = e.target.closest(".upsell-bundle__add");
    if (!submitter) return;

    const product = submitter.closest(".upsell-bundle__product");
    const quantity = +product.querySelector('quantity-selector [name="quantity"]')?.value || 1;
    const data = {
      type: "add",
      source: "product-page-upsell",
      sections: ["announcement-banner"],
      sections_url: "/",
      items: [
        {
          id: +submitter.dataset.id,
          quantity,
          product_id: +submitter.dataset.productId,
        },
      ],
      submitter,
      reload: false,
    };
    updateCart(data);
  }

  getPromotionAutoAdds() {
    const target = this.querySelector("[data-promotion-auto-adds]");
    if (!target) return [];

    try {
      const promotions = JSON.parse(target.textContent);
      return Array.isArray(promotions) ? promotions : [];
    } catch (error) {
      console.error("Unable to parse promotion auto-add products:", error);
      return [];
    }
  }

  parsePromotionDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  isPromotionActive(promotion, now) {
    const startDate = this.parsePromotionDate(promotion.startDate);
    const endDate = this.parsePromotionDate(promotion.endDate);

    if (startDate && now < startDate) return false;
    if (endDate && now >= endDate) return false;
    return true;
  }

  getActivePromotionItems(currentItems) {
    const now = new Date();
    const addedVariantIds = new Set(currentItems.map((item) => String(item.id)));
    const promotionItems = [];

    for (const promotion of this.promotionAutoAdds) {
      if (!this.isPromotionActive(promotion, now) || !Array.isArray(promotion.variantIds)) {
        continue;
      }

      for (const variantId of promotion.variantIds) {
        const id = String(variantId);
        if (!id || addedVariantIds.has(id)) continue;

        promotionItems.push({ id: variantId, quantity: 1 });
        addedVariantIds.add(id);
      }
    }

    return promotionItems;
  }

  hideAutoAddedUpsells() {
    const now = new Date();
    const activePromotionProductIds = new Set();
    const pendingClass = "upsell-bundle__product--promotion-pending";

    for (const promotion of this.promotionAutoAdds) {
      if (!this.isPromotionActive(promotion, now) || !Array.isArray(promotion.productIds)) {
        continue;
      }

      for (const productId of promotion.productIds) {
        activePromotionProductIds.add(String(productId));
      }
    }

    const upsellProducts = Array.from(this.querySelectorAll(".upsell-bundle__product"));
    for (const upsellProduct of upsellProducts) {
      const addButton = upsellProduct.querySelector(".upsell-bundle__add");
      if (addButton && activePromotionProductIds.has(addButton.dataset.productId)) {
        upsellProduct.remove();
      } else {
        upsellProduct.classList.remove(pendingClass);
      }
    }

    if (upsellProducts.length && !this.querySelector(".upsell-bundle__product")) {
      this.querySelector(".upsell-bundle__container")?.remove();
    }
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleVariantChanged.bind(this),
    );
    this.quantityChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.updateQuantity.bind(this),
    );
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.variantChangeUnsubscriber();
    this.quantityChangeUnsubscriber();
  }

  updateQuantity({ data }) {
    const { quantity, target } = data;

    if (this.contains(target)) {
      if (!data.upsell) {
        target
          .closest("quantity-selector")
          .nextElementSibling.querySelector('form [name="quantity"]').value = quantity;
      }
    }
  }

  async handleVariantChanged({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    // construct url with option values
    const productUrl = this.dataset.url;
    const url = this.getRequestUrl(productUrl, selectedOptionValues);
    const html = await this.fetchProductDOM(url);

    // replace specified DOM nodes
    for (const node of this.nodesToRender) {
      const target = node.id || node.selector;
      if (!target) continue;
      const original_node = this.querySelector(target);
      const new_node = html.querySelector(target);
      if (!original_node || !new_node) continue;
      original_node.parentNode.insertBefore(new_node, original_node);
      original_node.style.display = "none";

      setTimeout(() => original_node.remove(), 500);
    }

    // re-focus changed input
    document.querySelector(`#${target.id}`)?.focus();

    const variant_json = this.querySelector("[data-variant-json]");
    if (!variant_json) return;

    const variant = JSON.parse(variant_json.innerHTML);

    this.querySelector('.main-product__form input[name="id"]').value = variant.id;

    if (variant.featured_image) {
      this.updateVariantMedia(variant.featured_image);
    }

    this.updateHistory(variant.id);
    const data = {
      type: "variant-change",
      state: {
        available: { accept: variant.available },
      },
    };
    publish(PUB_SUB_EVENTS.productState, data);
  }

  getRequestUrl(url, optionValues = []) {
    const params = [];

    const section = ["section_id", this.dataset.sectionId];
    params.push(section);
    const existingParams = Array.from(new URLSearchParams(window.location.search));

    if (optionValues.length) {
      const values = ["option_values", optionValues.join(",")];
      params.push(values);
    }
    const fetchParams = new URLSearchParams(params);
    const newParams = new URLSearchParams([...fetchParams, ...existingParams]);

    return `${url}?${newParams.toString()}`;
  }

  async fetchProductDOM(url) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const res = await fetch(url, { signal: this.abortController.signal });
      const data = await res.text();
      const html = new DOMParser().parseFromString(data, "text/html");
      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted by user");
      } else {
        console.error(error);
      }
    }
  }

  updateVariantMedia(image) {
    this.media_gallery = this.querySelector("media-gallery");
    if (!this.media_gallery) return;

    const gallery_slide = this.media_gallery.querySelector(`[data-media="${image}"]`).dataset
      .swiperSlideIndex;

    this.media_gallery.swiper.slideTo(parseInt(gallery_slide));
  }

  updateHistory(id) {
    if (!history.replaceState) return;
    const url = `${this.dataset.url}${id ? `?variant=${id}` : ""}`;
    window.history.replaceState({}, "", url);
  }

  get nodesToRender() {
    return [
      {
        selector: "variant-select",
      },
      {
        selector: ".variant-sku",
      },
      {
        selector: ".product-notifications",
      },
      {
        selector: ".main-product__price",
      },
      {
        selector: ".main-product__sku",
      },
      {
        selector: "product-form .btn-elem",
      },
    ];
  }
}

customElements.define("main-product", MainProduct);

class BnplAccordions extends HTMLElement {
  connectedCallback() {
    const showPaymentInfo =
      new URLSearchParams(window.location.search).get("payment-info") === "true";
    if (!showPaymentInfo) return;

    const accordion = this.querySelector(".product-accordion");
    const content = accordion?.querySelector(".product-accordion__content");
    if (!accordion || !content) return;

    accordion.classList.add("accordion-open");
    content.style.height = "auto";
  }
}

customElements.define("bnpl-accordions", BnplAccordions);

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.state = {
      available: { accept: this.dataset.available === "true" ? true : false },
    };
  }

  connectedCallback() {
    this.productStateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.productState,
      this.updateProductState.bind(this),
    );
  }
  disconnectedCallback() {
    this.productStateUnsubscriber();
  }

  updateProductState({ type, state }) {
    this.state = { ...this.state, ...state };
    let error_state = "";
    const button = this.querySelector('button[type="submit"].btn-elem');

    // if variant change and no stock, ignore checks for other button states
    if ((type === "variant-change" && !this.state.available.accept) || !this.state.available.accept)
      return;

    for (const [key, value] of Object.entries(this.state)) {
      if (!value.accept) {
        error_state = key;
        break;
      }
    }
    if (error_state !== "") {
      button.classList.add("btn-elem--disabled");
      button.innerHTML = theme.strings[error_state];
      button.disabled = true;
    } else {
      button.classList.remove("btn-elem--disabled");
      button.innerHTML =
        this.dataset.preorder === "true" ? theme.strings.preorder : theme.strings.addToCart;
      button.disabled = false;
    }
  }
}

customElements.define("product-form", ProductForm);

class ProductFormSticky extends HTMLElement {
  constructor() {
    super();

    this.events = [
      {
        type: "scroll",
        el: window,
        callback: this.scrollHandler.bind(this),
      },
    ];
  }
  connectedCallback() {
    handleEventListeners(this.events, "add");
  }
  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }
  scrollHandler() {
    const container = document
      .querySelector("main-product .product-form__container")
      ?.getBoundingClientRect();
    if (!container) return;
    if (container.bottom < 0) {
      this.classList.add("sticky-visible");
    } else {
      this.classList.remove("sticky-visible");
    }
  }
}

customElements.define("product-form-sticky", ProductFormSticky);

class VariantSelect extends HTMLElement {
  constructor() {
    super();

    this.variant_json = this.querySelector("[data-variant-json]")
      ? JSON.parse(this.querySelector("[data-variant-json]").innerHTML)
      : null;
    this.events = [
      {
        el: this,
        type: "input",
        callback: this.variantChanged.bind(this),
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }
  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  variantChanged(e) {
    if (!this.variant_json) return;

    if (e.target.tagName === "SELECT") {
      e.target.querySelectorAll("option[selected]").forEach((option) => {
        option.removeAttribute("selected");
      });

      e.target
        .querySelector(`option[value="${e.target.value}"]`)
        .setAttribute("selected", "selected");
    }

    const values = this.selectedOptionValues;
    const target = e.target.tagName === "SELECT" ? e.target.selectedOptions[0] : e.target;

    this.updateSelectedTitle(e);

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: { event: e, target, selectedOptionValues: values },
    });
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }
  updateSelectedTitle({ target }) {
    const { value } = target;
    const selectedOptionTitle = target
      .closest(`.variant-select__group`)
      .querySelector("[data-selected-value]");
    if (selectedOptionTitle) selectedOptionTitle.innerHTML = value;
  }
}

customElements.define("variant-select", VariantSelect);
